import type { MessageDescriptor } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';

export type ObjectWorkspaceKind = 'patient' | 'visit';
export type ObjectWorkspaceFacet =
  | 'work'
  | 'details'
  | 'sessions'
  | 'history'
  | 'context'
  | 'plan'
  | 'documents';

export interface ObjectWorkspacePatientReference {
  id: string;
  name: string;
  sex?: string;
  age?: number;
  sourceSessionId?: string;
  patientProfileId?: string;
}

export interface ObjectWorkspaceObject {
  id: string;
  kind: ObjectWorkspaceKind;
  name: string;
  /** Source visit used to restore patient display data from a URL-backed identity. */
  sourceSessionId?: string;
  /** Real patient-profile id when the patient route was resolved from a session. */
  patientProfileId?: string;
  /** Patient attached to an encounter. Patient panes ignore this reference. */
  patient?: ObjectWorkspacePatientReference;
}

export interface ObjectWorkspacePatientDetails {
  birthDate?: string | null;
  gender?: string | null;
  email?: string | null;
  phone?: string | null;
  pastMedicalHistory?: string | null;
  currentMedications?: string | null;
  allergies?: string | null;
  additionalNotes?: string | null;
}

export type ObjectWorkspacePatientDetailsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; details: ObjectWorkspacePatientDetails };

export interface ObjectWorkspaceAnchor {
  id: string;
  kind: 'session' | 'history';
  title: string;
  occurredAt: string;
  detail?: string;
}

export interface ObjectWorkspaceAnchorSource {
  id: string;
  kind: 'session' | 'history';
  title: MessageDescriptor;
  occurredAt: string;
  detail?: MessageDescriptor;
}

export interface ObjectWorkspaceWorkItem {
  id: string;
  label: MessageDescriptor;
  timestamp: MessageDescriptor;
}

export interface ObjectWorkspaceWorkEngagement {
  id: string;
  title: string;
}

export interface ObjectWorkspaceAnchorSelection {
  anchor: ObjectWorkspaceAnchor;
  prompt: string;
  sequence: number;
}

export interface ObjectWorkspaceWorkSelection {
  work: ObjectWorkspaceWorkEngagement;
  sequence: number;
}

export const FACETS_BY_KIND: Record<
  ObjectWorkspaceKind,
  readonly { id: ObjectWorkspaceFacet; label: MessageDescriptor }[]
> = {
  patient: [
    { id: 'work', label: messages.work },
    { id: 'details', label: messages.details },
    { id: 'sessions', label: messages.sessions },
    { id: 'history', label: messages.history },
    { id: 'documents', label: messages.documents },
  ],
  visit: [
    { id: 'work', label: messages.work },
    { id: 'context', label: messages.context },
    { id: 'plan', label: messages.plan },
    { id: 'documents', label: messages.documents },
  ],
};

export const WORK_ITEMS_BY_KIND: Record<
  ObjectWorkspaceKind,
  readonly ObjectWorkspaceWorkItem[]
> = {
  patient: [
    {
      id: 'arrange-review',
      label: messages.arrangeReview,
      timestamp: messages.workAddedToday,
    },
    {
      id: 'review-recommendations',
      label: messages.reviewRecommendations,
      timestamp: messages.workAddedYesterday,
    },
    {
      id: 'recall-list',
      label: messages.workRecallList,
      timestamp: messages.workAddedEarlier,
    },
    {
      id: 'treatment-response',
      label: messages.reviewTreatmentResponse,
      timestamp: messages.workAddedLastWeek,
    },
  ],
  visit: [
    {
      id: 'visit-next-steps',
      label: messages.visitNextSteps,
      timestamp: messages.workAddedToday,
    },
    {
      id: 'visit-documents',
      label: messages.visitDraftDocuments,
      timestamp: messages.workAddedToday,
    },
    {
      id: 'visit-follow-up',
      label: messages.visitFollowUp,
      timestamp: messages.workAddedYesterday,
    },
    {
      id: 'visit-care-plan',
      label: messages.visitUpdatePlan,
      timestamp: messages.workAddedEarlier,
    },
  ],
};

export const PATIENT_SESSIONS: readonly ObjectWorkspaceAnchorSource[] = [
  {
    id: 'session-review',
    kind: 'session',
    title: messages.arrangeReview,
    occurredAt: '2026-09-03T09:30:00+10:00',
    detail: messages.newPatientVisit,
  },
  {
    id: 'session-diabetes',
    kind: 'session',
    title: messages.diabetesReview,
    occurredAt: '2026-08-18T11:00:00+10:00',
    detail: messages.followUpVisit,
  },
  {
    id: 'session-blood-pressure',
    kind: 'session',
    title: messages.firstBloodPressureAssessment,
    occurredAt: '2026-07-21T14:15:00+10:00',
    detail: messages.initialConsultation,
  },
];

export const PATIENT_HISTORY: readonly ObjectWorkspaceAnchorSource[] = [
  {
    id: 'history-review',
    kind: 'history',
    title: messages.arrangeReview,
    occurredAt: '2026-09-03T09:30:00+10:00',
  },
  {
    id: 'history-metformin',
    kind: 'history',
    title: messages.changedMetformin,
    occurredAt: '2026-08-18T11:20:00+10:00',
  },
  {
    id: 'history-prescription',
    kind: 'history',
    title: messages.sentPrescription,
    occurredAt: '2026-08-18T11:10:00+10:00',
  },
  {
    id: 'history-diagnosis',
    kind: 'history',
    title: messages.diagnosedDiabetes,
    occurredAt: '2026-05-12T10:00:00+10:00',
  },
];
